import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { api, ApiError } from '@/lib/axios-client'
import Button from '@/components/ui/Button'

// Demo adapter — overlay a pre-computed implicit-AI result.json onto an
// existing attachment, replacing only the implicit_ai_comment* rows
// for that AttachmentId. Calls
//   POST /SitePlanJobs/ImplicitAIComment/Import
// which is gated by IMPLICIT_AI_IMPORT_ENABLED on the backend (returns
// 404 when off, in which case this page just shows that error).
//
// REMOVE WHEN NARENDRA'S FULL PIPELINE LANDS — see
// .work-diary/may-04/20260504-implicit-ai-import-demo-adapter.md.

export const Route = createFileRoute('/(app)/admin/implicit-ai-import')({
  component: ImplicitAIImportPage,
})

interface ImportResponse {
  data: {
    implicitAICommentId: string
    findingsCount: number
    replacedExisting: boolean
    preservedBackgroundJobId: string | null
    cvcoreCacheEvicted: boolean
  }
}

interface ResultMeta {
  project?: string
  address?: string
  apn?: string
  zone?: string
  documents?: string[]
  generated?: string
  summary?: {
    PASS?: number
    FAIL?: number
    'NEEDS REVIEW'?: number
    total?: number
  }
}

interface ResultPayload {
  meta?: ResultMeta
  findings?: Array<{ category?: string; status?: string }>
}

interface AttachmentDto {
  id: string
  fileName: string
  contentType?: string
  fileSize?: number | null
  createdDate: string
  permitSubmittalId?: string | null
}

const DEMO_ATTACHMENT_ID = '9225a853-a7b2-4881-9362-87aca0218ba8'

async function sha256Prefix16(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  return hex.slice(0, 16)
}

function summariseResult(result: ResultPayload | null) {
  if (!result) return null
  const findings = result.findings ?? []
  const byCategory = findings.reduce<Record<string, number>>((acc, f) => {
    const cat = f.category ?? '(uncategorized)'
    acc[cat] = (acc[cat] ?? 0) + 1
    return acc
  }, {})
  return {
    total: findings.length,
    byCategory,
    summary: result.meta?.summary,
    project: result.meta?.project,
    address: result.meta?.address,
  }
}

function ImplicitAIImportPage() {
  const [attachmentId, setAttachmentId] = useState(DEMO_ATTACHMENT_ID)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [resultJson, setResultJson] = useState<ResultPayload | null>(null)
  const [resultJsonName, setResultJsonName] = useState<string>('')
  const [resultJsonError, setResultJsonError] = useState<string>('')
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfSha, setPdfSha] = useState<string>('')
  const [computingSha, setComputingSha] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [response, setResponse] = useState<ImportResponse['data'] | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Attachment picker state — load all attachments on mount, group by
  // permit submittal, let the user click one to fill the AttachmentId
  // field. Saves having to know the GUID.
  const [attachments, setAttachments] = useState<AttachmentDto[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [attachmentsError, setAttachmentsError] = useState<string>('')
  const [filter, setFilter] = useState<string>('')
  const [selectedPermit, setSelectedPermit] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    setAttachmentsLoading(true)
    // axios interceptor unwraps {data,isSuccess,errors} envelope, so the
    // payload is the raw AttachmentDto[] — declaring the response type as
    // an array directly is correct.
    api
      .get<AttachmentDto[]>('Attachments')
      .then((res) => {
        if (cancelled) return
        const list = Array.isArray(res) ? res : []
        const sorted = [...list].sort((a, b) =>
          (b.createdDate ?? '').localeCompare(a.createdDate ?? ''),
        )
        setAttachments(sorted)
      })
      .catch((err) => {
        if (cancelled) return
        setAttachmentsError(
          err instanceof ApiError
            ? `HTTP ${err.status ?? '?'} loading attachments`
            : err instanceof Error
              ? err.message
              : 'Failed to load attachments',
        )
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const groupedByPermit = useMemo(() => {
    const groups = new Map<string, AttachmentDto[]>()
    for (const att of attachments) {
      const key = att.permitSubmittalId ?? '(no permit)'
      const list = groups.get(key) ?? []
      list.push(att)
      groups.set(key, list)
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const aMax = Math.max(
        ...a[1].map((x) => new Date(x.createdDate ?? 0).getTime()),
      )
      const bMax = Math.max(
        ...b[1].map((x) => new Date(x.createdDate ?? 0).getTime()),
      )
      return bMax - aMax
    })
  }, [attachments])

  const filteredGroups = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    return groupedByPermit
      .filter(([permitId]) => !selectedPermit || permitId === selectedPermit)
      .map(([permitId, items]) => {
        const matched = needle
          ? items.filter(
              (a) =>
                a.fileName.toLowerCase().includes(needle) ||
                a.id.toLowerCase().includes(needle) ||
                permitId.toLowerCase().includes(needle),
            )
          : items
        return [permitId, matched] as const
      })
      .filter(([, items]) => items.length > 0)
  }, [groupedByPermit, filter, selectedPermit])

  const selectedAttachment = useMemo(
    () => attachments.find((a) => a.id === attachmentId) ?? null,
    [attachments, attachmentId],
  )

  const summary = useMemo(() => summariseResult(resultJson), [resultJson])

  const canSubmit =
    !submitting &&
    !!attachmentId.trim() &&
    !!resultJson &&
    !!resultJson.findings &&
    resultJson.findings.length > 0

  async function onResultJsonPicked(e: ChangeEvent<HTMLInputElement>) {
    setResultJsonError('')
    setResultJson(null)
    setResultJsonName('')
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const parsed = JSON.parse(text) as ResultPayload
      if (!parsed?.findings || !Array.isArray(parsed.findings)) {
        throw new Error('Missing top-level "findings" array.')
      }
      if (!parsed?.meta?.summary) {
        throw new Error('Missing "meta.summary".')
      }
      setResultJson(parsed)
      setResultJsonName(file.name)
    } catch (err) {
      setResultJsonError(
        err instanceof Error ? err.message : 'Failed to parse JSON.',
      )
    }
  }

  async function onPdfPicked(e: ChangeEvent<HTMLInputElement>) {
    setPdfSha('')
    setPdfFile(null)
    const file = e.target.files?.[0]
    if (!file) return
    setPdfFile(file)
    setComputingSha(true)
    try {
      const sha = await sha256Prefix16(file)
      setPdfSha(sha)
    } catch (err) {
      setResultJsonError(
        `SHA-256 failed: ${err instanceof Error ? err.message : 'unknown'}`,
      )
    } finally {
      setComputingSha(false)
    }
  }

  async function onSubmit() {
    if (!resultJson) return
    setSubmitting(true)
    setErrorMessage('')
    setResponse(null)
    try {
      const body = {
        attachmentId: attachmentId.trim(),
        pdfSha: pdfSha || null,
        pageNumber,
        result: resultJson,
      }
      const res = await api.post<ImportResponse>(
        'SitePlanJobs/ImplicitAIComment/Import',
        body,
      )
      setResponse(res.data)
    } catch (err) {
      if (err instanceof ApiError) {
        const status = err.status ?? 'no-status'
        const detail =
          (
            err.response?.data as { errors?: Array<{ message: string }> }
          )?.errors
            ?.map((e) => e.message)
            .join(' / ') ?? err.message
        setErrorMessage(`HTTP ${status}: ${detail}`)
      } else if (err instanceof Error) {
        setErrorMessage(err.message)
      } else {
        setErrorMessage('Unknown error')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <h1 className="text-heading-lg font-semibold mb-2">Implicit-AI Import</h1>
      <p className="text-body text-gray-600 dark:text-gray-400 mb-6">
        Demo adapter — overlay a pre-computed implicit-AI{' '}
        <code className="bg-gray-100 dark:bg-(--color-surface-subtle) px-1 py-0.5 rounded">
          result.json
        </code>{' '}
        onto an existing attachment. Replaces only{' '}
        <code className="bg-gray-100 dark:bg-(--color-surface-subtle) px-1 py-0.5 rounded">
          implicit_ai_comment*
        </code>{' '}
        rows for that AttachmentId; preserves the original BackgroundJobId.
      </p>

      <div className="mb-6 rounded border border-amber-300 bg-amber-50 p-3 text-body text-amber-900">
        <strong>Throwaway tooling.</strong> Disabled on prod via{' '}
        <code>IMPLICIT_AI_IMPORT_ENABLED</code>. Removed when Narendra&apos;s
        full pipeline lands.
      </div>

      <fieldset
        disabled={submitting}
        className="space-y-5 rounded border border-gray-200 dark:border-(--color-border) p-5"
      >
        <div>
          <span className="block text-body font-medium mb-1">
            Pick the attachment
          </span>
          <div className="mb-2 flex gap-2">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by file name, attachment id, or permit submittal id…"
              className="flex-1 rounded border border-gray-300 dark:border-(--color-border) px-3 py-2 text-body focus:border-blue-500 focus:outline-none"
            />
            <select
              value={selectedPermit}
              onChange={(e) => setSelectedPermit(e.target.value)}
              className="rounded border border-gray-300 dark:border-(--color-border) px-3 py-2 text-body focus:border-blue-500 focus:outline-none"
            >
              <option value="">All permits</option>
              {groupedByPermit.map(([permitId, items]) => (
                <option key={permitId} value={permitId}>
                  {permitId === '(no permit)'
                    ? '(no permit)'
                    : `Permit ${permitId.slice(0, 8)}…`}{' '}
                  ({items.length})
                </option>
              ))}
            </select>
          </div>

          {attachmentsLoading && (
            <div className="text-body-sm text-gray-500 dark:text-gray-400">
              Loading attachments…
            </div>
          )}
          {attachmentsError && (
            <div className="text-body-sm text-red-600">{attachmentsError}</div>
          )}

          <div className="max-h-72 overflow-y-auto rounded border border-gray-200 dark:border-(--color-border) bg-white dark:bg-(--color-surface)">
            {filteredGroups.length === 0 && !attachmentsLoading && (
              <div className="p-3 text-body text-gray-500 dark:text-gray-400">
                No attachments match.
              </div>
            )}
            {filteredGroups.map(([permitId, items]) => (
              <div
                key={permitId}
                className="border-b border-gray-100 dark:border-(--color-border) last:border-b-0"
              >
                <div className="bg-gray-50 dark:bg-(--color-surface-subtle) px-3 py-1.5 font-mono text-body-sm text-gray-600 dark:text-gray-400">
                  Permit submittal:{' '}
                  {permitId === '(no permit)' ? '(none)' : permitId}
                </div>
                <ul>
                  {items.map((att) => {
                    const isSelected = att.id === attachmentId
                    return (
                      <li key={att.id}>
                        <button
                          type="button"
                          onClick={() => setAttachmentId(att.id)}
                          className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-body hover:bg-blue-50 ${
                            isSelected ? 'bg-blue-100' : ''
                          }`}
                        >
                          <span className="truncate font-medium">
                            {att.fileName}
                          </span>
                          <span className="shrink-0 font-mono text-body-sm text-gray-500 dark:text-gray-400">
                            {att.id.slice(0, 8)}… ·{' '}
                            {new Date(att.createdDate).toLocaleString()}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-body font-medium mb-1">
            Selected Attachment ID
          </span>
          <input
            type="text"
            value={attachmentId}
            onChange={(e) => setAttachmentId(e.target.value)}
            placeholder="UUID of the attachment in qpoint-backend"
            className="w-full rounded border border-gray-300 dark:border-(--color-border) px-3 py-2 font-mono text-body focus:border-blue-500 focus:outline-none"
          />
          {selectedAttachment ? (
            <span className="mt-1 block text-body-sm text-green-700">
              ✓ {selectedAttachment.fileName} (permit{' '}
              {selectedAttachment.permitSubmittalId
                ? selectedAttachment.permitSubmittalId.slice(0, 8) + '…'
                : 'none'}
              )
            </span>
          ) : (
            <span className="mt-1 block text-body-sm text-gray-500 dark:text-gray-400">
              Pick from the list above, or paste a GUID directly. Demo default:{' '}
              {DEMO_ATTACHMENT_ID}.
            </span>
          )}
        </label>

        <label className="block">
          <span className="block text-body font-medium mb-1">
            result.json <span className="text-red-500">*</span>
          </span>
          <input
            type="file"
            accept="application/json,.json"
            onChange={onResultJsonPicked}
            className="block w-full text-body file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-body file:font-medium file:text-blue-700 hover:file:bg-blue-100"
          />
          {resultJsonName && (
            <span className="mt-1 block text-body-sm text-gray-500 dark:text-gray-400">
              Loaded: <code>{resultJsonName}</code>
            </span>
          )}
          {resultJsonError && (
            <span className="mt-1 block text-body-sm text-red-600">
              {resultJsonError}
            </span>
          )}
        </label>

        {summary && (
          <div className="rounded bg-gray-50 dark:bg-(--color-surface-subtle) p-3 text-body">
            <div className="font-medium mb-1">Preview</div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Project:</span>{' '}
              {summary.project || '(none)'}
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Address:</span>{' '}
              {summary.address || '(none)'}
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                Findings:
              </span>{' '}
              {summary.total} total — PASS {summary.summary?.PASS ?? 0} / FAIL{' '}
              {summary.summary?.FAIL ?? 0} / NEEDS REVIEW{' '}
              {summary.summary?.['NEEDS REVIEW'] ?? 0}
            </div>
            <div className="mt-2">
              <span className="text-gray-600 dark:text-gray-400">
                By category:
              </span>
              <ul className="ml-4 list-disc">
                {Object.entries(summary.byCategory).map(([cat, n]) => (
                  <li key={cat}>
                    {cat}: {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <label className="block">
          <span className="block text-body font-medium mb-1">
            PDF (optional) — for cv-core cache eviction
          </span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={onPdfPicked}
            className="block w-full text-body file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-body file:font-medium file:text-blue-700 hover:file:bg-blue-100"
          />
          {pdfFile && (
            <span className="mt-1 block text-body-sm text-gray-500 dark:text-gray-400">
              {pdfFile.name} —{' '}
              {computingSha ? 'computing SHA-256…' : `pdfSha = ${pdfSha}`}
            </span>
          )}
          <span className="mt-1 block text-body-sm text-gray-500 dark:text-gray-400">
            Without this, a re-run of the deployed pipeline could overwrite the
            import from cv-core&apos;s Redis cache. Don&apos;t re-run during
            demo.
          </span>
        </label>

        <label className="block">
          <span className="block text-body font-medium mb-1">
            PDF page number
          </span>
          <input
            type="number"
            min={1}
            value={pageNumber}
            onChange={(e) => setPageNumber(Number(e.target.value) || 1)}
            className="w-32 rounded border border-gray-300 dark:border-(--color-border) px-3 py-2 text-body focus:border-blue-500 focus:outline-none"
          />
          <span className="mt-1 block text-body-sm text-gray-500 dark:text-gray-400">
            Which page of the PDF the implicit-AI check ran on. Used as part of
            cv-core&apos;s Redis cache key{' '}
            <code>
              implicit_comments_v4:&#123;pdfSha&#125;:p&#123;page&#125;
            </code>{' '}
            so the right cached envelope is evicted. Single-sheet site plans:
            leave at 1.
          </span>
        </label>

        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {submitting ? 'Importing…' : 'Import'}
        </Button>
      </fieldset>

      {response && (
        <div className="mt-6 rounded border border-green-300 bg-green-50 p-4 text-body">
          <div className="mb-2 font-semibold text-green-900">
            Import succeeded
          </div>
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              New comment id:
            </span>{' '}
            <code className="font-mono">{response.implicitAICommentId}</code>
          </div>
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              Findings written:
            </span>{' '}
            {response.findingsCount}
          </div>
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              Replaced existing:
            </span>{' '}
            {response.replacedExisting ? 'yes' : 'no'}
          </div>
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              Preserved BackgroundJobId:
            </span>{' '}
            <code className="font-mono">
              {response.preservedBackgroundJobId ?? '(none)'}
            </code>
          </div>
          <div>
            <span className="text-gray-700 dark:text-gray-300">
              cv-core cache evicted:
            </span>{' '}
            {response.cvcoreCacheEvicted
              ? 'yes'
              : 'no — re-run could overwrite'}
          </div>
          <div className="mt-3 text-body-sm text-green-900">
            Refresh the playground tab for this attachment — the AI Checklist
            Comments panel should now render the rich categorized findings.
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-6 rounded border border-red-300 bg-red-50 p-4 text-body">
          <div className="mb-1 font-semibold text-red-900">Import failed</div>
          <div className="font-mono whitespace-pre-wrap text-red-800">
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  )
}
